import React, { useMemo } from 'react';
import { Text, Box, Skeleton } from "@mantine/core";
import { Card } from "@xplatform/design-system";
import { LineChart } from '@mantine/charts';
import type { CostTimeSeriesViewModel } from '@models/cost';
import './UserCountChart.scss';

interface UserCountChartProps {
    data: CostTimeSeriesViewModel | null;
    isLoading: boolean;
}

const UserCountChart: React.FC<UserCountChartProps> = ({ data, isLoading }) => {
    const chartData = useMemo(() => {
        if (!data) return [];
        return data.dataPoints.map((point) => ({
            timestamp: new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                hour: data.granularity === 'hour' ? 'numeric' : undefined,
            }).format(point.timestamp),
            'Active Users': point.usersCount,
        }));
    }, [data]);

    const averageUsers = useMemo(() => {
        if (!data || data.dataPoints.length === 0) return 0;
        const total = data.dataPoints.reduce((sum, point) => sum + point.usersCount, 0);
        return Math.round(total / data.dataPoints.length);
    }, [data]);

    if (isLoading) {
        return (
            <Card>
                <Skeleton height={32} width="40%" mb="sm" />
                <Skeleton height={300} mt="sm" />
            </Card>
        );
    }

    if (!data || chartData.length === 0) {
        return (
            <Card>
                <Text fw={600} size="lg" mb={4}>Active Users Trend</Text>
                <Text>No data available. Select a date range to view user trends.</Text>
            </Card>
        );
    }

    return (
        <Card className="user-count-chart-card" withBorder>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <Box>
                    <Text fw={600} size="lg" mb={4}>Active Users Trend</Text>
                    <Text size="sm" c="dimmed">
                        {data.granularity.charAt(0).toUpperCase() + data.granularity.slice(1)} breakdown
                    </Text>
                </Box>
                <Box className="total-indicator">
                    <Text size="sm" c="dimmed">Average Users</Text>
                    <Text fw={600} size="lg" c="success">{averageUsers}</Text>
                </Box>
            </Box>
            <LineChart
                h={300}
                data={chartData}
                dataKey="timestamp"
                series={[{ name: 'Active Users', color: 'teal.6' }]}
                curveType="monotone"
                withLegend
                withDots
                yAxisProps={{ allowDecimals: false }}
            />
        </Card>
    );
};

export default UserCountChart;
